import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  // NEW: track whether the user has manually interacted with this accordion
  #userHasInteracted = false;

  connectedCallback() {
    const { signal } = this.#controller;

    // Only set default open state if user hasn't interacted yet
    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // NEW: listen for the native toggle event to know when user interacts
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    // NEW: reset interaction flag when removed from DOM
    this.#userHasInteracted = false;
  }

  // NEW: mark interaction on native toggle
  #handleToggle = () => {
    this.#userHasInteracted = true;
  };

  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  #handleContentClick = (event) => {
    if (!this.details.open) return;
    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;
    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  #handleMediaQueryChange = () => {
    // NEW: only reset to default if the user hasn't manually opened/closed this accordion
    if (!this.#userHasInteracted) {
      this.#setDefaultOpenState();
    }
  };

  #setDefaultOpenState() {
    // NEW: don't override user's manual state
    if (this.#userHasInteracted) return;

    const isMobile = isMobileBreakpoint();
    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
